import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiLogIn, FiLogOut } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { assets } from "../../../../assets/assets";
import { EmailInviteLanguage } from "../../../../autogen/bff-api";
import { getPreferredLanguage, setPreferredLanguage } from "../../../../common/local-storage/language";
import { useAppDispatch, useAppSelector } from "../../../../common/redux/hooks";
import { logoutThunk } from "../../../../common/redux/thunks/auth/logout-thunk";
import { urls } from "../../../../urls";
import { getBuyerInitials } from "../utils/displayFormatters";

interface Props {
  children: React.ReactNode;
}

export const PortalLayout = ({ children }: Props) => {
  const { t } = useTranslation();
  const theme = useColorMode();
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.authState.state);
  const loggedInPerson = authState && authState.type !== "LoggedOut" ? authState.me : null;
  const isNorwegian = getPreferredLanguage() === EmailInviteLanguage.No;

  const switchLanguage = (language: EmailInviteLanguage) => {
    setPreferredLanguage(language);
    window.location.reload();
  };

  return (
    <Box minH="100vh" bg="smBackgroundSecondary">
      <Box borderTop="3px solid" borderTopColor="smPrimary" bg="smBackground" borderBottom="1px solid" borderBottomColor="smBorder">
        <Flex width="full" maxW="container.xl" mx="auto" align="center" justify="space-between" px={{ base: "4", md: "6" }} py="3">
          <Image src={theme.colorMode === "dark" ? assets.logo_dark : assets.logo} alt="SourceMagnet" h="28px" />

          <HStack spacing={{ base: "3", md: "5" }}>
            <Link
              as={RouterLink}
              to={urls.portal.list}
              color="smPrimary"
              fontSize="sm"
              fontWeight="semibold"
              display={{ base: "none", md: "inline-flex" }}
              _hover={{ textDecoration: "none", color: "smPrimarySoft" }}
            >
              {t("Procurements")}
            </Link>

            <Divider orientation="vertical" height="24px" borderColor="smBorder" display={{ base: "none", md: "block" }} />

            <HStack spacing="1.5" fontSize="sm" fontWeight="semibold">
              <LangOption label="NO" active={isNorwegian} onSelect={() => switchLanguage(EmailInviteLanguage.No)} />
              <Text color="smBorder">/</Text>
              <LangOption label="EN" active={!isNorwegian} onSelect={() => switchLanguage(EmailInviteLanguage.En)} />
            </HStack>

            <Divider orientation="vertical" height="24px" borderColor="smBorder" display={{ base: "none", md: "block" }} />

            {loggedInPerson ? (
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="ghost"
                  rightIcon={<Icon as={FiChevronDown} color="smMuted" />}
                  _hover={{ bg: "smBackgroundSecondary" }}
                  _active={{ bg: "smBackgroundSecondary" }}
                >
                  <HStack spacing="2">
                    <Flex
                      width="28px"
                      height="28px"
                      rounded="full"
                      bg="smPrimarySofter"
                      color="smPrimary"
                      fontSize="11px"
                      fontWeight="bold"
                      align="center"
                      justify="center"
                    >
                      {getBuyerInitials(`${loggedInPerson.firstName} ${loggedInPerson.lastName}`)}
                    </Flex>
                    <Text fontSize="sm" fontWeight="semibold" display={{ base: "none", md: "block" }}>
                      {loggedInPerson.firstName} {loggedInPerson.lastName}
                    </Text>
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem icon={<Icon as={FiLogOut} />} onClick={() => dispatch(logoutThunk())}>
                    {t("Log out")}
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <Button
                as={RouterLink}
                to={urls.login}
                size="sm"
                variant="outline"
                color="smPrimary"
                leftIcon={<Icon as={FiLogIn} />}
                borderColor="smBorder"
                _hover={{ bg: "smPrimarySofter" }}
              >
                {t("Log in")}
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      <Box width="full" maxW="container.xl" mx="auto" px={{ base: "4", md: "6" }} py={{ base: "6", md: "8" }}>
        {children}
      </Box>

      <Flex justify="center" px="4" py="8">
        <Text color="smMuted" fontSize="sm">
          © 2026 SourceMagnet · {" "}
          <Link href="https://www.sourcemagnet.com/privacy-policy" isExternal color="smMuted" textDecoration="underline">
            {t("Privacy Policy")}
          </Link>
        </Text>
      </Flex>
    </Box>
  );
};

const LangOption = ({ label, active, onSelect }: { label: string; active: boolean; onSelect: () => void }) =>
  active ? (
    <Text color="smPrimary" aria-current="true">
      {label}
    </Text>
  ) : (
    <Box as="button" onClick={onSelect} color="smMuted" _hover={{ color: "smPrimary" }}>
      {label}
    </Box>
  );
